import { useState } from "react";
import { Notice, useNotice } from "../components/Notice";
import { PageHeader } from "../components/PageHeader";
import { Chip, ErrorState, Skeleton } from "../components/States";
import { publishTerms, termsInfo } from "../lib/agreements-data";
import { BRAND } from "../lib/config";
import { fmtDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";

/** #/terms: the terms clients sign. Directors publish the version in the client layer. */
export function Terms() {
  const { data, error, loading, reload } = useAsync(termsInfo);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  async function publish() {
    setBusy(true);
    setNotice(null);
    try {
      await publishTerms(label.trim());
      ok(`Published as ${label.trim()}. New signing links use this version.`);
      setLabel("");
      reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const title = data?.title ?? BRAND.termsTitle;
  return (
    <section className="page job-grid" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Agreements"
        title={title}
        meta={`Clients accept these when they sign a quote. Each signature records the version in force. Once a version is published, bookings need a signed quote (or a recorded reason) before they can be confirmed.`}
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {loading && !data && <Skeleton rows={3} columns={2} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && (
        <>
          <div className="card">
            <div className="card__head">
              <h2 className="card__title">Ready to publish</h2>
              {data.draft ? (data.draft.alreadyPublished ? <Chip tone="green">Published</Chip> : <Chip tone="accent">Not published yet</Chip>) : <Chip tone="outline">None</Chip>}
            </div>
            <div className="card__body stack">
              {!data.draft && <p className="muted">There is no {title.toLowerCase()} text set up for this site yet. Your platform provider adds it.</p>}
              {data.draft && !data.draft.alreadyPublished && (
                <>
                  <p>Read the text below carefully. Publishing makes it the version clients sign from now on. Earlier signatures keep the version they signed.</p>
                  <div className="addcrew">
                    <input className="input" placeholder="Version name, e.g. 2026-10" aria-label="Version name" value={label} onChange={(e) => setLabel(e.target.value)} />
                    <button type="button" className="btn btn--primary btn--sm" disabled={busy || !label.trim()} aria-busy={busy} onClick={() => void publish()}>
                      {busy ? "Publishing" : "Publish this version"}
                    </button>
                  </div>
                  <p className="small muted">Only a director can publish.</p>
                </>
              )}
              {data.draft && <pre className="terms-text">{data.draft.markdown}</pre>}
            </div>
          </div>

          <div className="card">
            <div className="card__head"><h2 className="card__title">Published versions</h2></div>
            {data.published.length === 0 ? (
              <p className="card__body muted">Nothing published yet. Quotes cannot be sent for signing until a version is published.</p>
            ) : (
              <ul className="list">
                {data.published.map((p, i) => (
                  <li key={p.id} className="list__row">
                    <span><span className="strong">{p.versionLabel}</span> <span className="sub">published {fmtDate(p.publishedAt)} · SHA-256 {p.sha256.slice(0, 16)}...</span></span>
                    {i === 0 && <Chip tone="green">In force</Chip>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
